import React, {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import {
	Close,
	Done,
	FileUploadOutlined,
	FolderRounded,
	Link,
	Warning,
} from '@mui/icons-material'
import { useSearchParams } from 'react-router-dom'
import cn from '../../../../lib/utils/cn'
import toastCaller from '../../../../lib/utils/toastCaller'
import type {
	UploadDiscoveryState,
	UploadFileStatus,
} from '../../../../context/progressContext'
import { getUploadProgressSummary } from '../../../../lib/utils/upload'
import { Button, IconButton, ProgressBar, Tooltip } from '../../../ui'
import Popup from '../../../ui/Popup'
import { ConfirmationModal } from '../../common'
import FilterButtons from './FilterButtons'
import UploadTreeList from './UploadTreeList'

interface UploadProgressPopupProps {
	files: UploadFileStatus[]
	discoveryState: UploadDiscoveryState
	isOpen: boolean
	onClose: () => void
	onCancel: (fileId: string | null) => void
}

type ItemsFilter =
	| 'all'
	| 'pending'
	| 'uploading'
	| 'completed'
	| 'canceled'
	| 'failed'

const UPLOAD_POPUP_MIN_HEIGHT = 250
const UPLOAD_POPUP_MAX_HEIGHT = 650
const UPLOAD_POPUP_DEFAULT_HEIGHT = 500

const UploadProgressPopup: React.FC<UploadProgressPopupProps> = ({
	files,
	discoveryState,
	isOpen,
	onClose,
	onCancel,
}) => {
	const [filter, setFilter] = useState<ItemsFilter>('all')
	const [isCancelRestModalOpen, setIsCancelRestModalOpen] = useState(false)
	const [copiedFileId, setCopiedFileId] = useState<string | null>(null)
	const [popupHeight, setPopupHeight] = useState(UPLOAD_POPUP_DEFAULT_HEIGHT)
	const [isPopupExpanded, setIsPopupExpanded] = useState(true)
	const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const resizeStartRef = useRef<{ height: number; y: number } | null>(null)
	const [, setSearchParams] = useSearchParams()

	useEffect(() => {
		if (!isOpen) return

		setPopupHeight(UPLOAD_POPUP_DEFAULT_HEIGHT)
		setIsPopupExpanded(true)
	}, [isOpen])

	const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
		if (event.pointerType === 'mouse' && event.button !== 0) return

		resizeStartRef.current = { height: popupHeight, y: event.clientY }
		event.currentTarget.setPointerCapture(event.pointerId)
	}

	const handleResizeMove = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!resizeStartRef.current) return

		const nextHeight =
			resizeStartRef.current.height + resizeStartRef.current.y - event.clientY
		setPopupHeight(
			Math.min(
				UPLOAD_POPUP_MAX_HEIGHT,
				Math.max(UPLOAD_POPUP_MIN_HEIGHT, nextHeight),
			),
		)
	}

	const handleResizeEnd = (event: React.PointerEvent<HTMLDivElement>) => {
		resizeStartRef.current = null
		if (event.currentTarget.hasPointerCapture(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId)
		}
	}

	const uploadSummary = useMemo(
		() => getUploadProgressSummary(files, discoveryState),
		[files, discoveryState],
	)
	const {
		fileItems,
		hasActiveUploads,
		isUploadFinished,
		completionState,
		completionLabel,
		overallProgress,
	} = uploadSummary
	const completionCircleClass =
		completionState === 'failed'
			? 'bg-negative'
			: completionState === 'warning'
				? 'bg-warning'
				: 'bg-positive'

	const filterCounts = useMemo<Record<ItemsFilter, number>>(
		() => ({
			all: fileItems.length,
			pending: uploadSummary.counts.pending,
			uploading: uploadSummary.counts.uploading,
			completed: uploadSummary.counts.completed,
			canceled: uploadSummary.counts.canceled,
			failed: uploadSummary.counts.failed,
		}),
		[fileItems.length, uploadSummary.counts],
	)

	const handleCopyLink = useCallback((id: string) => {
		const url = `${window.location.origin}/aura/preview?is_blank=true&id=${id}`
		navigator.clipboard.writeText(url).then(() => {
			toastCaller({
				type: 'success',
				message: 'URL copied to clipboard!',
				withClosePrevious: true,
			})
			if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
			setCopiedFileId(id)
			copyTimerRef.current = setTimeout(() => setCopiedFileId(null), 1300)
		})
	}, [])

	useEffect(
		() => () => {
			if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
		},
		[],
	)

	const renderActions = (item: UploadFileStatus) => {
		const isLoading = item.status === 'pending' || item.status === 'uploading'
		if (isLoading) {
			return (
				<Button
					variant="secondary"
					label="Cancel"
					onClick={() => item.id && onCancel(item.id)}
					tooltip="Cancel upload"
					className="h-8 box-border"
				/>
			)
		}

		if (item.nodeType === 'folder') {
			return item.createdOnServer ? (
				<IconButton
					variant="secondary"
					className="w-8 h-8 box-border"
					icon={<FolderRounded className="w-4 h-4 dark:fill-text-main-dark" />}
					onClick={() => setSearchParams({ id: item.currentFolderId })}
					tooltip="Open folder"
				/>
			) : null
		}

		if (item.status !== 'completed') return null

		return (
			<div className="flex items-center gap-1">
				<IconButton
					variant="secondary"
					className="w-8 h-8 box-border"
					icon={<FolderRounded className="w-4 h-4 dark:fill-text-main-dark" />}
					onClick={() => setSearchParams({ id: item.currentFolderId })}
					tooltip="Open folder"
				/>
				<IconButton
					variant="secondary"
					className="w-8 h-8 box-border"
					icon={
						<span className="relative inline-flex items-center justify-center h-5 w-5">
							<Link
								className={cn(
									'w-5 h-5 dark:fill-text-main-dark absolute transition-all duration-200',
									copiedFileId === item.fileId && 'scale-0 opacity-0',
								)}
							/>
							<Done
								className={cn(
									'w-5 h-5 fill-positive absolute transition-all duration-200',
									copiedFileId === item.fileId
										? 'scale-100 opacity-100'
										: 'scale-0 opacity-0',
								)}
							/>
						</span>
					}
					onClick={() => item.fileId && handleCopyLink(item.fileId)}
					tooltip={copiedFileId === item.fileId ? 'Copied!' : 'Copy link'}
				/>
			</div>
		)
	}

	const filterButtons: { label: string; value: ItemsFilter }[] = [
		{ label: 'All', value: 'all' },
		{ label: 'Pending', value: 'pending' },
		{ label: 'Uploading', value: 'uploading' },
		{ label: 'Completed', value: 'completed' },
		{ label: 'Canceled', value: 'canceled' },
		{ label: 'Failed', value: 'failed' },
	]

	if (!isOpen) return null

	return (
		<>
			<Popup
				isOpen={isOpen}
				onClose={onClose}
				expanded={isPopupExpanded}
				onExpandedChange={setIsPopupExpanded}
				icon={
					<FileUploadOutlined className="fill-text-secondary w-5 h-5 dark:fill-text-main-dark" />
				}
				relativePosition={true}
				title={
					<Fragment>
						<span className="dark:text-white">Uploads</span>
						<Tooltip
							label={
								isUploadFinished
									? completionLabel
									: discoveryState.isDiscovering
										? `Calculating files and folders. Overall upload progress: ${overallProgress}%`
										: `Overall upload progress: ${overallProgress}%`
							}
						>
							<div
								className={cn(
									'relative h-6 shrink-0 transition-all duration-upload-complete ease-out',
									isUploadFinished ? 'w-6' : 'w-28',
								)}
							>
								<div
									className={cn(
										'absolute left-0 top-1/2 -translate-y-1/2 w-full overflow-hidden transition-all duration-upload-complete ease-out',
										isUploadFinished
											? 'h-6 rounded-full'
											: 'h-1.5 rounded-full',
										isUploadFinished ? completionCircleClass : 'bg-accent/20',
									)}
								>
									{discoveryState.isDiscovering && fileItems.length === 0 ? (
										<ProgressBar
											value={overallProgress}
											maxValue={100}
											color="#ff8412"
											isLoading
											classNameWrapper="h-full rounded-full"
										/>
									) : (
										<div
											className={cn(
												'h-full rounded-full',
												isUploadFinished ? completionCircleClass : 'bg-accent',
											)}
											style={{
												width: `${overallProgress}%`,
												transition: isUploadFinished
													? 'width 700ms ease-out, background-color 700ms ease-out'
													: 'width 300ms ease-out, background-color 700ms ease-out',
											}}
										/>
									)}
								</div>
								<div
									className={cn(
										'absolute left-0 top-0 w-6 h-6 flex items-center justify-center transition-all duration-100 ease-out',
										isUploadFinished
											? 'opacity-100 delay-upload-complete-icon'
											: 'opacity-0 delay-0',
									)}
								>
									{completionState === 'failed' ? (
										<Close className="w-4 h-4 fill-white" />
									) : completionState === 'warning' ? (
										<Warning className="w-4 h-4 fill-stroke-light-dark" />
									) : (
										<Done className="w-4 h-4 fill-white" />
									)}
								</div>
							</div>
						</Tooltip>
						{hasActiveUploads && (
							<div className="ml-3 md:hidden">
								<Button
									variant="secondary"
									label="Cancel all"
									tooltip="Cancel active uploads"
									onClick={() => setIsCancelRestModalOpen(true)}
									className="h-8"
								/>
							</div>
						)}
					</Fragment>
				}
				className={cn(
					'max-sm:w-[calc(100%+24px)] max-sm:-mx-3 dark:text-text-main-dark dark:bg-background-light-dark sm:w-[620px] sm:min-w-0 sm:max-w-[calc(100vw-40px)] overflow-hidden',
					isPopupExpanded &&
						'sm:h-[var(--upload-popup-height)] sm:min-h-[350px] sm:max-h-[650px]',
				)}
				style={
					{
						'--upload-popup-height': `${popupHeight}px`,
					} as React.CSSProperties
				}
			>
				<div
					className="absolute top-0 left-1/2 hidden sm:flex w-16 h-5 -translate-x-1/2 items-center justify-center cursor-ns-resize touch-none z-10"
					onPointerDown={handleResizeStart}
					onPointerMove={handleResizeMove}
					onPointerUp={handleResizeEnd}
					onPointerCancel={handleResizeEnd}
					aria-label="Resize upload panel"
				>
					<span className="w-9 h-1 rounded-full bg-text-secondary/60" />
				</div>
				<div className="flex flex-col flex-1 min-h-0 w-full min-w-0">
					<div className="flex flex-wrap items-center gap-x-2 md:[&>div]:flex-1 md:[&>div]:w-auto mt-2">
						<FilterButtons
							buttons={filterButtons}
							activeFilter={filter}
							onFilterChange={setFilter}
							counts={filterCounts}
						/>
						{hasActiveUploads && (
							<Button
								variant="custom"
								label="Cancel all"
								tooltip="Cancel active uploads"
								onClick={() => setIsCancelRestModalOpen(true)}
								className="hidden md:flex h-8 bg-negative text-white hover:bg-negative/80"
							/>
						)}
					</div>
					{files.length > 0 ? (
						<UploadTreeList
							items={files}
							filter={filter}
							renderActions={renderActions}
						/>
					) : (
						<div className="flex items-center justify-center min-h-40 px-0.5 mt-3">
							<p className="text-text-secondary text-xs">No items</p>
						</div>
					)}
				</div>
			</Popup>

			<ConfirmationModal
				overlayClassName="!z-[1000]"
				title="Cancel all uploads?"
				description="This will cancel all pending and uploading files."
				isOpen={isCancelRestModalOpen}
				onClose={() => setIsCancelRestModalOpen(false)}
				onAction={() => {
					onCancel(null)
					setIsCancelRestModalOpen(false)
				}}
			/>
		</>
	)
}

export default UploadProgressPopup
